import React, { useState, useEffect } from 'react';
import axiosInstance from '@/lib/axios';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, 
  Users, 
  Building2, 
  TrendingUp, 
  Calculator,
  FileText,
  PlusCircle,
  Search,
  Filter,
  Download,
  Mail,
  Phone,
  Euro,
  Calendar,
  BarChart3,
  Briefcase,
  Star,
  Clock,
  CheckCircle2,
  AlertCircle,
  Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ConseillerProPage() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddClient, setShowAddClient] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // New client form
  const [newClient, setNewClient] = useState({
    name: '',
    email: '',
    phone: '',
    budget: '',
    investmentGoal: 'cashflow', // cashflow, patrimoine, defiscalisation
    notes: ''
  });

  useEffect(() => {
    loadUserInfo();
    loadClients();
  }, []);

  const loadUserInfo = async () => {
    try {
      const response = await axiosInstance.get(`${API}/auth/me`, { withCredentials: true });
      setCurrentUser(response.data);
      
      // Check if user is Pro (VIP or has pro access)
      if (!response.data.is_vip && !response.data.is_pro) {
        toast.error('Accès réservé aux conseillers Pro');
        navigate('/dashboard');
      }
    } catch (error) {
      navigate('/');
    }
  };

  const loadClients = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`${API}/conseiller/clients`, { withCredentials: true });
      setClients(response.data.clients || []);
    } catch (error) {
      // If endpoint doesn't exist yet, use mock data
      setClients([
        {
          client_id: 'demo_1',
          name: 'Jean Dupont',
          email: 'jean.dupont@email.com',
          phone: '06 12 34 56 78',
          budget: 250000,
          investmentGoal: 'cashflow',
          status: 'active',
          simulations: 3,
          created_at: '2025-01-15'
        },
        {
          client_id: 'demo_2',
          name: 'Marie Martin',
          email: 'marie.martin@email.com',
          phone: '06 98 76 54 32',
          budget: 400000,
          investmentGoal: 'patrimoine',
          status: 'pending',
          simulations: 1,
          created_at: '2025-02-01'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClient = async () => {
    if (!newClient.name || !newClient.email) {
      toast.error('Nom et email requis');
      return;
    }

    try {
      const response = await axiosInstance.post(`${API}/conseiller/clients`, newClient, { 
        withCredentials: true 
      });
      
      setClients(prev => [response.data.client, ...prev]);
      setNewClient({ name: '', email: '', phone: '', budget: '', investmentGoal: 'cashflow', notes: '' });
      setShowAddClient(false);
      toast.success('Client ajouté !');
    } catch (error) {
      // Fallback for demo
      const demoClient = {
        client_id: `client_${Date.now()}`,
        ...newClient,
        status: 'new',
        simulations: 0,
        created_at: new Date().toISOString()
      };
      setClients(prev => [demoClient, ...prev]);
      setNewClient({ name: '', email: '', phone: '', budget: '', investmentGoal: 'cashflow', notes: '' });
      setShowAddClient(false);
      toast.success('Client ajouté !');
    }
  };

  const createSimulationForClient = (clientId) => {
    navigate(`/simulation/new?client=${clientId}`);
  };

  const sendReportToClient = async (clientId, simulationId) => {
    toast.success('Rapport envoyé au client par email');
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || client.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      new: 'bg-blue-100 text-blue-700',
      inactive: 'bg-slate-100 text-slate-600'
    };
    const labels = {
      active: 'Actif',
      pending: 'En attente',
      new: 'Nouveau',
      inactive: 'Inactif'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.inactive}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getGoalLabel = (goal) => {
    const labels = {
      cashflow: '💰 Cash-flow',
      patrimoine: '🏛️ Patrimoine',
      defiscalisation: '📉 Défiscalisation'
    };
    return labels[goal] || goal;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-600 border-t-transparent mx-auto mb-4" />
          <p className="text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Briefcase className="h-7 w-7" />
                  Mode Conseiller Pro
                </h1>
                <p className="text-indigo-200 text-sm">Gérez vos clients et leurs investissements</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-white/10 rounded-lg px-4 py-2">
                <p className="text-sm text-indigo-200">Clients actifs</p>
                <p className="text-2xl font-bold">{clients.filter(c => c.status === 'active').length}</p>
              </div>
              <div className="bg-white/10 rounded-lg px-4 py-2">
                <p className="text-sm text-indigo-200">Simulations</p>
                <p className="text-2xl font-bold">{clients.reduce((sum, c) => sum + (c.simulations || 0), 0)}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {/* Actions Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Rechercher un client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white"
              data-testid="search-clients"
            />
          </div>
          <div className="flex gap-3">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-lg bg-white text-slate-700"
              data-testid="filter-status"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actifs</option>
              <option value="pending">En attente</option>
              <option value="new">Nouveaux</option>
            </select>
            <Button
              onClick={() => setShowAddClient(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="btn-add-client"
            >
              <PlusCircle className="mr-2 h-4 w-4" />
              Nouveau client
            </Button>
          </div>
        </div>

        {/* Add Client Modal */}
        {showAddClient && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl">
              <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
                <Users className="h-6 w-6 text-indigo-600" />
                Ajouter un client
              </h3>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-700 text-sm">Nom complet *</Label>
                    <Input
                      value={newClient.name}
                      onChange={(e) => setNewClient({...newClient, name: e.target.value})}
                      placeholder="Jean Dupont"
                      data-testid="input-client-name"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 text-sm">Email *</Label>
                    <Input
                      type="email"
                      value={newClient.email}
                      onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                      placeholder="jean@email.com"
                      data-testid="input-client-email"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-700 text-sm">Téléphone</Label>
                    <Input
                      value={newClient.phone}
                      onChange={(e) => setNewClient({...newClient, phone: e.target.value})}
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 text-sm">Budget (€)</Label>
                    <Input
                      type="number"
                      value={newClient.budget}
                      onChange={(e) => setNewClient({...newClient, budget: e.target.value})}
                      placeholder="250000"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-slate-700 text-sm">Objectif d'investissement</Label>
                  <select
                    value={newClient.investmentGoal}
                    onChange={(e) => setNewClient({...newClient, investmentGoal: e.target.value})}
                    className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg"
                  >
                    <option value="cashflow">💰 Générer du cash-flow</option>
                    <option value="patrimoine">🏛️ Construire un patrimoine</option>
                    <option value="defiscalisation">📉 Défiscaliser</option>
                  </select>
                </div>
                
                <div>
                  <Label className="text-slate-700 text-sm">Notes</Label>
                  <textarea
                    value={newClient.notes}
                    onChange={(e) => setNewClient({...newClient, notes: e.target.value})}
                    placeholder="Notes sur le client..."
                    className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg h-20 resize-none"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <Button
                  onClick={() => setShowAddClient(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleAddClient}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="btn-save-client"
                >
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Ajouter
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Clients Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClients.map(client => (
            <div 
              key={client.client_id}
              className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg transition-shadow"
              data-testid={`client-card-${client.client_id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-slate-800 text-lg">{client.name}</h3>
                  <p className="text-sm text-slate-500">{getGoalLabel(client.investmentGoal)}</p>
                </div>
                {getStatusBadge(client.status)}
              </div>
              
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Mail className="h-4 w-4 text-slate-400" />
                  {client.email}
                </div>
                {client.phone && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Phone className="h-4 w-4 text-slate-400" />
                    {client.phone}
                  </div>
                )}
                {client.budget && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Euro className="h-4 w-4 text-slate-400" />
                    Budget: {parseInt(client.budget).toLocaleString('fr-FR')} €
                  </div>
                )}
              </div>
              
              <div className="flex items-center justify-between py-3 border-t border-slate-100">
                <div className="flex items-center gap-1 text-sm text-slate-500">
                  <BarChart3 className="h-4 w-4" />
                  {client.simulations || 0} simulation{(client.simulations || 0) > 1 ? 's' : ''}
                </div>
                <div className="text-xs text-slate-400">
                  Depuis {new Date(client.created_at).toLocaleDateString('fr-FR')}
                </div>
              </div>
              
              <div className="flex gap-2 mt-4">
                <Button
                  onClick={() => createSimulationForClient(client.client_id)}
                  size="sm"
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid={`btn-new-sim-${client.client_id}`}
                >
                  <Calculator className="mr-1 h-4 w-4" />
                  Simulation
                </Button>
                <Button
                  onClick={() => sendReportToClient(client.client_id)}
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  data-testid={`btn-send-report-${client.client_id}`}
                >
                  <Send className="mr-1 h-4 w-4" />
                  Envoyer
                </Button>
              </div>
            </div>
          ))}
        </div>

        {filteredClients.length === 0 && (
          <div className="text-center py-16">
            <Users className="h-16 w-16 text-slate-200 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-600 mb-2">Aucun client trouvé</h3>
            <p className="text-slate-400 mb-6">Commencez par ajouter votre premier client</p>
            <Button
              onClick={() => setShowAddClient(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              <PlusCircle className="mr-2 h-4 w-4" />
              Ajouter un client
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
